import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import {
  clearChunkReloadAttempt,
  isChunkLoadError,
  reloadOnceForStaleChunk,
} from '../utils/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  capturedError: Error | null;
  exhausted: boolean;
}

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { capturedError: null, exhausted: false };
  }

  static getDerivedStateFromError(error: Error): State {
    if (!isChunkLoadError(error)) {
      return { capturedError: error, exhausted: false };
    }
    if (reloadOnceForStaleChunk()) {
      return { capturedError: error, exhausted: false };
    }
    return { capturedError: error, exhausted: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      console.warn('[ChunkLoadErrorBoundary]', error.message);
      return;
    }
    try {
      Sentry.captureException(error, {
        tags: { source: 'chunk_load_error_boundary' },
      });
    } catch {
      // ignore
    }
  }

  render(): ReactNode {
    const { capturedError, exhausted } = this.state;

    if (capturedError && !isChunkLoadError(capturedError)) {
      throw capturedError;
    }

    if (exhausted) {
      clearChunkReloadAttempt();
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#ffffff',
            color: '#111827',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: '28rem' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
              Having trouble loading?
            </h1>
            <p style={{ color: '#6b7280', margin: 0 }}>
              Please clear your browser cache and refresh.
            </p>
          </div>
        </div>
      );
    }

    if (capturedError) {
      return null;
    }

    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
